import { Client } from "@/Common/Api/Client";
import { FileType } from "@/Common/Models/FileType";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { InfoFaxReceiverContactOverlayElement } from "@/ExportableFile/Models/OverlayElements/InfoFaxReceiverContactOverlayElement";
import { OverlayElementType } from "@/ExportableFile/Models/OverlayElements/OverlayElement";
import { BaseGetExportableFileFetchUrlResponse } from "../BaseGetExportableFileFetchUrlResponse";
import { ExportableFile, IExportableFile, IExportableFileLoaded } from "../ExportableFile";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

interface IFileTypeExamRequest extends IExportableFile {
    ExamRequestId: string;
}

class FileTypeExamRequest extends ExportableFile implements IFileTypeExamRequest {
    public canGenerateFileXcom: boolean = true;

    public ExamRequestId: string;

    constructor(fields: IFileTypeExamRequest) {
        super(FileType.EXAMREQUEST);
        Object.assign(this, fields);
        this.allowedDraggableImageType = [];
    }

    public getAttachedObject_ObjectId(): string { return this.ExamRequestId; }

    public getCleanCopy(): FileTypeExamRequest {
        let copyConstructor: IFileTypeExamRequest = {
            ExamRequestId: this.ExamRequestId,
            date: this.date,
            description: this.description
        };

        return new FileTypeExamRequest(copyConstructor);
    }

    public async getMeta(): Promise<number> {
        throw "Not implemented";
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({ includeOverlays: includeOverlays, isExternal: true });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const { includeOverlays, isExternal } = generateFileOption;
        const requestedOverlays = (includeOverlays && this.overlayElements) || [];

        const response =
            isExternal
                ? await Client.postAsyncXcom<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetExamRequestFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(),
                        ExamRequestId: this.ExamRequestId
                    },
                    this.clientTag,
                    this.claimId
                )
                : await Client.postAsync<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetExamRequestFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(),
                        ExamRequestId: this.ExamRequestId
                    }
                );

        if (!response.isSuccess)
            return;

        this.totalPages = response.data.TotalPages;

        const tempFile = new FileTypeTemporaryFile({
            ObjectId: response.data.ObjectId,
            totalPages: this.totalPages,
            sizeBytes: response.data.SizeBytes,
            date: this.date,
            description: this.description
        });

        tempFile.isExternal = isExternal;
        if (tempFile.isExternal) {
            tempFile.clientTag = this.clientTag;
            tempFile.claimId = this.claimId;
        }

        super.setCache(tempFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: tempFile,
            FetchUrl:
                isExternal
                    ? tempFile.FetchUrlXcom(this.clientTag, this.claimId)
                    : tempFile.FetchUrl
        } as IExportableFileLoaded;
    }

    public async persistSignature(): Promise<void> {
        await Client.postAsync<{ SignedPrintAuthorization: SignedPrintAuthorization }>(
            ServerOperations.Server.Myle_SaveSignedPrintAuthorization,
            { RequestId: this.ExamRequestId }
        );
    }

    public trySetFaxContactInfo(name: string, faxNumber: string): boolean {
        const faxInfoContact = this._overlayElements.firstOrDefault(p => p.overlayElementType === OverlayElementType.InfoFaxReceiverContact) as InfoFaxReceiverContactOverlayElement;
        if (!faxInfoContact) {
            this._overlayElements.push(new InfoFaxReceiverContactOverlayElement({ Name: name, FaxNumber: faxNumber }));
            return true;
        }

        const isSame = name === faxInfoContact?.Name
            && faxNumber === faxInfoContact?.FaxNumber;

        if (!isSame) {
            faxInfoContact.Name = name;
            faxInfoContact.FaxNumber = faxNumber;
            return true;
        }

        return false;
    }
}

export { FileTypeExamRequest };
